import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  ButtonSelect,
  FooterActionBar,
  Icon,
  Loader,
  type MenuOption,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodAdvancedFilters,
  SodHeader,
  SodMessage,
} from '../../../../shared/components';
import {
  AAG_SOD_ENTITLEMENT,
  DEFAULT_AD_FILTER_PARAMS,
  testIds,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../../shared/hooks';
import {
  type ActiveAdvancedFilterState,
  type AdvancedFilterState,
  type RootState,
} from '../../../../shared/types';
import { createDevRejectionHandler } from '../../../../shared/utils';
import { buildColumnDisplay } from '../../../ViolationsReport/utils/columnConfig';
import {
  useGetApplicationEndpointsMutation,
  useGetEntitlementsMutation,
  useGetRisksMutation,
  useLazyGetEntitlementTypesQuery,
} from '../../../ViolationsTab/data-layer/violationsTabApi';
import {
  ENT_REPORT_TABS,
  FILTER_CONFIGS,
  RISK_DETAILS_TAB_INDEX,
  SEARCH_DEBOUNCE,
  TOGGLE_BTNS,
  USER_DETAILS_TAB,
  USER_DETAILS_TAB_INDEX,
} from '../../constants';
import { entReportsActions } from '../../data-store/entReportsSlice';
import useExportReport from '../../hooks/use-entExportReport';
import useExportStatusPolling from '../../hooks/use-entExportStatusPolling';
import useReportColumnsPicker from '../../hooks/use-entReportColumnsPicker';
import useEntReportPreview from '../../hooks/use-entReportPreview';
import useEntSyncExport from '../../hooks/use-entSyncExport';
import {
  type EntColumnConfig,
  type EntReportPreviewProps,
  type EntReportTabType,
} from '../../types';
import EntReportTable from '../EntReportTable/EntReportTable';

import './EntReportPreview.css';

/**
 * EntReportPreview Component
 *
 * Comprehensive entitlement report preview interface with tab-based report type selection,
 * advanced filtering, column configuration, and export functionality. Manages both risk
 * and user detail reports with real-time data preview and export status tracking.
 *
 * @component
 * @returns {React.ReactElement} Report preview interface with features including:
 *   - Tab-based report type selection (Risk Details/User Details)
 *   - Advanced filtering with search and dropdown options
 *   - Real-time report data preview with pagination
 *   - Export functionality with progress tracking
 *   - Column configuration and ordering
 *   - Breadcrumb navigation and modal integration
 *
 * @example
 * <EntReportPreview setShowReportModal={setModalVisible} />
 */
function EntReportPreview({
  setShowReportModal,
}: EntReportPreviewProps): ReactElement {
  const l = useInvertedLocalize();
  const localize = useLocalize();
  const dispatch = useDispatch();
  const { navigateToUrl } = useNavigateTo();

  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Get preferred tab from Redux state
  const { preferredReportTab } = useTypedSelector((state) => state.aag.details);

  const [activeTab, setActiveTab] = useState<number>(
    preferredReportTab === USER_DETAILS_TAB
      ? (USER_DETAILS_TAB_INDEX as number)
      : (RISK_DETAILS_TAB_INDEX as number)
  );
  const [columnConfig, setColumnConfig] = useState<EntColumnConfig>({
    selectedColumns: [],
    columnOrder: {},
  });

  // Clean up preferred tab on component mount
  useEffect(() => {
    if (preferredReportTab) {
      dispatch({ type: 'aag/details/setPreferredReportTab', payload: null });
    }
  }, [preferredReportTab, dispatch]);

  // Get initial filters from entitlement tab state only once on mount
  const entitlementTabFilters = useTypedSelector(
    (state) => state.aag.entitlement.controls.activeFilters
  );
  const [initialFilters] = useState(entitlementTabFilters);

  // Clear selected entitlement and filters when navigating between different routes
  useEffect(() => {
    const currentPath = window.location.pathname;

    return () => {
      const newPath = window.location.pathname;

      // Clear only if navigating to a different route (not just modal changes)
      if (currentPath !== newPath) {
        dispatch(entReportsActions.setSelectedEntitlementValueKey(null));
        dispatch(entReportsActions.setSelectedEntitlementName(null));
        // Clear all filters on page navigation
        dispatch(entReportsActions.setRiskDetailsFilters([]));
        dispatch(entReportsActions.setUserDetailsFilters([]));
      }
    };
  }, [dispatch]);

  const { exportProgress } = useTypedSelector(
    (state: RootState) => state.aag.entReports
  );

  const [isColumnsAndFiltersLoaded, setColumnsAndFiltersLoaded] =
    useState(false);
  const [isColumnsLoading, setIsColumnsLoading] = useState(false);

  const {
    reportData,
    isLoading,
    isReportError,
    selectedEntitlementValueKey,
    selectedEntitlementName,
    activeExports,
    riskDetailsFilters,
    userDetailsFilters,
    exportStatusByReportType,
    columnsByReportType,
  } = useTypedSelector((state: RootState) => state.aag.entReports);

  const entitlementRisks = useTypedSelector(
    (state) => state.aag.details.entitlementDetails.data?.riskIds
  );

  const pageClasses = useMemo(
    () =>
      cx(
        'Sod-Ent-Report Sod-Report',
        isFilterExpanded && 'Sod-Report--isFilterExpanded'
      ),
    [isFilterExpanded]
  );

  const { exportReport, isExporting } = useExportReport();
  const { startPolling, stopPolling } = useExportStatusPolling();
  const { exportSyncReport, isExporting: isSyncExporting } = useEntSyncExport();

  // Get current tab's API endpoint
  const currentReportType = TOGGLE_BTNS[activeTab - 1]?.value;

  // Use columns picker hook
  const { fetchColumns } = useReportColumnsPicker();

  // Use preview hook
  const { getEntReportPreview } = useEntReportPreview();

  // Filter setters for Redux
  const setRiskDetailsFilters = useCallback(
    (filters: ActiveAdvancedFilterState[]) => {
      dispatch(entReportsActions.setRiskDetailsFilters(filters));
    },
    [dispatch]
  );
  const setUserDetailsFilters = useCallback(
    (filters: ActiveAdvancedFilterState[]) => {
      dispatch(entReportsActions.setUserDetailsFilters(filters));
    },
    [dispatch]
  );

  // Initialize local filters with relevant initial filters only once per tab
  const [initializedTabs, setInitializedTabs] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    if (initialFilters.length > 0 && !initializedTabs.has(currentReportType)) {
      // Filter only relevant filters for current tab
      const relevantFilters = initialFilters.filter((filter) => {
        const currentTabFilters = FILTER_CONFIGS[currentReportType];

        return currentTabFilters.some(
          (tabFilter) => tabFilter.value === filter.value
        );
      });

      if (relevantFilters.length > 0) {
        if (currentReportType === ENT_REPORT_TABS.RISK_DETAILS) {
          dispatch(entReportsActions.setRiskDetailsFilters(relevantFilters));
        } else {
          dispatch(entReportsActions.setUserDetailsFilters(relevantFilters));
        }
      }

      setInitializedTabs((prev) => new Set(prev).add(currentReportType));
    }
  }, [initialFilters, currentReportType, dispatch, initializedTabs]);

  // Use local filters as the source of truth
  const activeFilters: ActiveAdvancedFilterState[] =
    currentReportType === ENT_REPORT_TABS.RISK_DETAILS
      ? riskDetailsFilters
      : userDetailsFilters;

  const setActiveFilters = useMemo(
    () =>
      currentReportType === ENT_REPORT_TABS.RISK_DETAILS
        ? setRiskDetailsFilters
        : setUserDetailsFilters,
    [currentReportType, setRiskDetailsFilters, setUserDetailsFilters]
  );

  // Tab-specific filters based on available columns
  const filters = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!currentReportType) {
      return [];
    }

    const filterConfig = FILTER_CONFIGS[currentReportType];

    return filterConfig;
  }, [currentReportType]);

  const reportKey = useMemo(
    () =>
      typeof currentReportType === 'string'
        ? currentReportType.toUpperCase()
        : '',
    [currentReportType]
  );

  const exportProgressValue = useMemo(
    () => exportProgress[currentReportType] || 0,
    [exportProgress, currentReportType]
  );

  // Check if there's an active export for current report type only
  const hasActiveExport = useMemo(
    () =>
      Array.isArray(activeExports) &&
      activeExports.some(
        (e: { reportType: string }) => e.reportType === currentReportType
      ),
    [activeExports, currentReportType]
  );

  // Check export status for current report type only
  const currentExportStatus = exportStatusByReportType[currentReportType];

  const [pollPct, setPollPct] = useState(0);
  const [isExtendedLoading, setIsExtendedLoading] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const successTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local progress with API progress
  useEffect(() => {
    const apiProgress = exportProgress[currentReportType] || 0;

    setPollPct(apiProgress);

    setIsExtendedLoading(isExporting && apiProgress < 100);
  }, [exportProgress, currentReportType, isExporting]);

  // Stop polling when reaching 100 and mark success.
  useEffect(() => {
    if (!isExtendedLoading) {
      return;
    }

    if (pollPct < 100) {
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsExtendedLoading(false);
  }, [isExtendedLoading, pollPct]);

  // Auto-reset success after 3 seconds and clear export progress
  useEffect(() => {
    if (exportProgressValue >= 100) {
      successTimerRef.current = setTimeout(() => {
        dispatch(
          entReportsActions.setExportProgress({
            reportType: currentReportType,
            progressPercentage: 0,
          })
        );
      }, 3000);
    }

    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, [exportProgressValue, dispatch, currentReportType]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    },
    []
  );

  // API hooks for filter search
  const [fetchRiskFilters] = useGetRisksMutation();
  const [fetchEntitlements] = useGetEntitlementsMutation();
  const [fetchEntitlementTypes] = useLazyGetEntitlementTypesQuery();
  const [fetchApplicationName] = useGetApplicationEndpointsMutation();

  const getFilterController = useCallback(
    (
      key: string,
      params: { search: string; key: string; max: string; offset: string }
    ) => {
      const controllers = {
        riskName: () => fetchRiskFilters(params),
        entitlementValue: () => fetchEntitlements(params),
        entitlementTypeName: () => fetchEntitlementTypes(params),
        endpointName: () => fetchApplicationName(params),
      };

      return controllers[key as keyof typeof controllers]();
    },
    [
      fetchRiskFilters,
      fetchEntitlements,
      fetchEntitlementTypes,
      fetchApplicationName,
    ]
  );

  const handleFilterSearch = useCallback(
    async <T extends string>(
      key: T,
      searchText: T
    ): Promise<MenuOption[] | undefined | false> => {
      if (!searchText) {
        return [];
      }

      try {
        const params = { ...DEFAULT_AD_FILTER_PARAMS, search: searchText, key };
        const controller = getFilterController(key, params);

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-misused-promises
        if (!controller) {
          return [];
        }

        const response = await controller.unwrap();

        return response as MenuOption[];
      } catch (error) {
        return undefined;
      }
    },
    [getFilterController]
  );

  const handleOptionsClick = useCallback(
    (key: string, method: string, selectedOptions: MenuOption[]) => {
      if (selectedOptions.length === 0) {
        const newFilters = activeFilters.filter(
          (filter) => filter.value !== key
        );

        setActiveFilters(newFilters);

        return;
      }

      const isFilterAvailable = activeFilters.find(
        (filter) => filter.value === key
      );
      const newActiveFilters = isFilterAvailable
        ? activeFilters.map((filter) =>
            filter.value === key
              ? { ...filter, options: selectedOptions }
              : filter
          )
        : [
            ...activeFilters,
            {
              value: key,
              method,
              options: selectedOptions,
            },
          ];

      setActiveFilters(newActiveFilters);
    },
    [activeFilters, setActiveFilters]
  );

  const handleFilterChange = useCallback(
    (
      selectedFilter: Pick<
        AdvancedFilterState,
        'value' | 'isChecked' | 'method'
      >
    ) => {
      const isFilterApplied = activeFilters.find(
        (filter) => filter.value === selectedFilter.value
      );

      if (selectedFilter.isChecked && isFilterApplied) {
        handleOptionsClick(selectedFilter.value, selectedFilter.method, []);
      }

      // Filter checked state is now managed by the constant filters
    },
    [activeFilters, handleOptionsClick]
  );

  const handleClearBtn = useCallback(() => {
    setActiveFilters([]);
  }, [setActiveFilters]);

  const isClearBtn = useMemo(() => activeFilters.length > 0, [activeFilters]);

  // Clear report data when tab changes and set selectedReport
  useEffect(() => {
    dispatch(entReportsActions.setEntReportData([]));
    dispatch(entReportsActions.setSelectedReport(currentReportType));
  }, [currentReportType, dispatch]);

  // Initialize columns and filters
  useEffect(() => {
    const initializeColumnsAndFilters = async () => {
      try {
        setIsColumnsLoading(true);
        await fetchColumns(currentReportType);
        setColumnsAndFiltersLoaded(true);
      } catch (error) {
        setColumnsAndFiltersLoaded(false);
      } finally {
        setIsColumnsLoading(false);
      }
    };

    initializeColumnsAndFilters().catch(
      createDevRejectionHandler('EntReportPreview: initializeColumnsAndFilters')
    );
  }, [currentReportType, fetchColumns]);

  // Convert active filters to API format
  const convertFiltersToApiFormat = useCallback(
    (filtersItems: ActiveAdvancedFilterState[]) => {
      const selectedRiskIds =
        currentReportType === ENT_REPORT_TABS.RISK_DETAILS &&
        entitlementRisks &&
        selectedEntitlementValueKey
          ? (entitlementRisks as [])
          : [];

      const apiFilters = {
        entitlement: {
          entitlementValueKeys: selectedEntitlementValueKey
            ? [selectedEntitlementValueKey]
            : ([] as string[]),
          entitlementTypeKeys: [] as string[],
          entitlementDisplayName: [] as string[],
          entitlementTypeName: [] as string[],
          entitlementValue: [] as string[],
          entitlementSoxSeverity: [] as string[],
          entitlementSysSeverity: [] as string[],
          entitlementPrivilegeSeverity: [] as string[],
        },
        risk: {
          riskIds: selectedRiskIds as string[],
          riskType: [] as string[],
          riskName: [] as string[],
          riskPriority: [] as string[],
        },
        endpoint: {
          endpointKeys: [] as string[],
          endpointName: [] as string[],
        },
        globalSearch: '',
      };

      filtersItems.forEach((filter) => {
        const values = filter.options.map((option) => option.value);

        switch (filter.value) {
          case 'riskName':
            apiFilters.risk.riskName = values;
            break;
          case 'riskType':
            apiFilters.risk.riskType = values;
            break;
          case 'riskPriority':
            apiFilters.risk.riskPriority = values;
            break;
          case 'entitlementValue':
            apiFilters.entitlement.entitlementValue = values;
            break;
          case 'entitlementTypeName':
            apiFilters.entitlement.entitlementTypeName = values;
            break;
          case 'entitlementSoxSeverity':
            apiFilters.entitlement.entitlementSoxSeverity = values;
            break;
          case 'entitlementSysSeverity':
            apiFilters.entitlement.entitlementSysSeverity = values;
            break;
          case 'entitlementPrivilegeSeverity':
            apiFilters.entitlement.entitlementPrivilegeSeverity = values;
            break;
          case 'endpointName':
            apiFilters.endpoint.endpointName = values;
            break;
        }
      });

      return apiFilters;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentReportType, selectedEntitlementValueKey]
  );

  // Fetch preview data when columns are loaded, tab changes, or filters change
  useEffect(() => {
    const fetchReportData = async () => {
      const apiFilters = convertFiltersToApiFormat(activeFilters);

      await getEntReportPreview(
        { reportType: currentReportType, filters: apiFilters },
        currentReportType
      );
    };

    if (isColumnsAndFiltersLoaded) {
      fetchReportData().catch(
        createDevRejectionHandler('EntReportPreview: fetchReportData')
      );
    }
  }, [
    isColumnsAndFiltersLoaded,
    currentReportType,
    activeFilters,
    getEntReportPreview,
    convertFiltersToApiFormat,
  ]);

  // Auto-start polling when there are active exports
  useEffect(() => {
    if (activeExports.length > 0) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [activeExports, startPolling, stopPolling]);

  const columnDisplayForExport = useMemo(
    () =>
      buildColumnDisplay(
        columnConfig.selectedColumns,
        columnsByReportType?.[currentReportType as EntReportTabType],
        localize
      ),
    [
      columnConfig.selectedColumns,
      columnsByReportType,
      currentReportType,
      localize,
    ]
  );

  const handleExport = async () => {
    const exportFilters = convertFiltersToApiFormat(activeFilters);

    const payload = {
      reportType: currentReportType,
      format: 'EXCEL' as const,
      filters: exportFilters,
      columnConfig: {
        columnOrder: columnConfig.columnOrder,
        selectedColumns: columnConfig.selectedColumns,
        columnDisplay: columnDisplayForExport,
      },
      formattingOptions: {
        excelOptions: {
          cellFormatting: {
            data: {
              dateFormat: 'yyyy-MM-dd HH:mm:ss',
              numberFormat: '#,##0.00',
            },
          },
        },
      },
      fileName: `Sod_Entitlement_Report_${new Date().getFullYear()}`,
      timezone: 'UTC',
      locale: 'en',
    };

    await exportReport(payload);
    // Polling will start automatically when activeExports is updated
  };

  const handleSyncExport = async () => {
    const exportFilters = convertFiltersToApiFormat(activeFilters);

    const payload = {
      reportType: currentReportType,
      format: 'EXCEL' as const,
      filters: {
        ...exportFilters,
        entitlementValueKeys: selectedEntitlementValueKey
          ? [selectedEntitlementValueKey]
          : ([] as string[]),
      },
      columnConfig: {
        columnOrder: columnConfig.columnOrder,
        selectedColumns: columnConfig.selectedColumns,
        columnDisplay: columnDisplayForExport,
      },
    };

    await exportSyncReport(payload);
  };

  const headerParams = {
    title: selectedEntitlementName
      ? `${l('Entitlements with Inherent Risks')}: ${selectedEntitlementName}`
      : l('Entitlements with Inherent Risks'),
    breadcrumbItems: [
      { text: l('Violations Workbench'), href: AAG_SOD_ENTITLEMENT },
      {
        text: l('Report'),
        onClick: () => setShowReportModal(true),
      },
    ],
  };

  const [filterKey, setFilterKey] = useState(
    `${currentReportType}-${Date.now()}`
  );

  // Update filter key when tab changes to force complete re-render
  useEffect(() => {
    setFilterKey(`${currentReportType}-${Date.now()}`);
  }, [currentReportType]);

  const renderedFilters = (
    <SodAdvancedFilters
      key={filterKey}
      filters={filters}
      activeFilters={activeFilters}
      isClearBtn={isClearBtn}
      searchDebounce={SEARCH_DEBOUNCE}
      isAddOrRemoveBtn={false}
      handleFilterChange={handleFilterChange}
      handleFilterSearch={handleFilterSearch}
      handleOptionsClick={handleOptionsClick}
      handleClearBtn={handleClearBtn}
      isDisabled={isLoading || isSyncExporting || isExporting}
    />
  );

  const renderHeader = () => (
    <SodHeader
      title={headerParams.title}
      controls={null}
      className='Sod-Report-header'
      breadcrumbItems={headerParams.breadcrumbItems}
      handleGoBack={() => navigateToUrl(AAG_SOD_ENTITLEMENT)}
    />
  );

  const renderBody = () => (
    <Box className='Sod-Report-content'>
      <Box className='Sod-Report-tabsAndFilter'>
        <ToggleButtonGroup
          selection={activeTab}
          setSelection={setActiveTab}
          className='Sod-Report-TabGroup'
          isDisabled={isLoading || isSyncExporting || isExporting}>
          {TOGGLE_BTNS.map((btn) => (
            <ToggleButton
              key={btn.id}
              label={l(btn.label)}
              isDisabled={isLoading || isSyncExporting || isExporting}
            />
          ))}
        </ToggleButtonGroup>

        <ButtonSelect
          label={l('Filter')}
          kind='rectangle'
          onClick={() => {
            setIsFilterExpanded(!isFilterExpanded);
          }}
          size='medium'
          isOpen={isFilterExpanded}
          prefixIcon={<Icon kind='Filter' />}
          isVisualCueEnabled={false}
          BadgeComp={
            isClearBtn ? (
              <Badge kind='dot' color='primary' size='small' />
            ) : null
          }
          isDisabled={isLoading || isSyncExporting || isExporting}
        />
      </Box>

      {isFilterExpanded && (
        <Box className='Sod-Ent-Report-filtersBlock'>{renderedFilters}</Box>
      )}

      {isReportError && (
        <Box tag='div' className='Sod-Report-errorContainer'>
          <Typography>
            {l('Error loading report data, Please try again later.')}
          </Typography>
        </Box>
      )}

      {!isReportError && (isLoading || isColumnsLoading) && (
        <Box className='Sod-Report-loader'>
          <Loader kind='loop' format='main' color='brand' />
        </Box>
      )}

      {!isReportError &&
        !isLoading &&
        !isColumnsLoading &&
        isColumnsAndFiltersLoaded && (
          <>
            {reportData.length === 0 ? (
              <Box className='Sod-Report-noRecordFound'>
                <SodMessage
                  isSearchActive={false}
                  isFilterActive={false}
                  handleFilterClearBtn={handleClearBtn}
                  defaultTitle={l('No Records')}
                  defaultMessage={l(
                    'No Record Found. Change some filters and try again.'
                  )}
                />
              </Box>
            ) : (
              <EntReportTable onColumnConfigChange={setColumnConfig} />
            )}
          </>
        )}
    </Box>
  );

  const renderFooter = () => (
    <FooterActionBar className='Sod-Report-footerActionBar'>
      <Button
        type='button'
        kind='subtle'
        onClick={() => setShowReportModal(true)}
        isDisabled={isLoading || isSyncExporting || isExporting}>
        {l('View All Reports')}
      </Button>
      <Box className='Sod-Report-footerGroupRight'>
        {activeExports.some((exp) => exp.reportType === currentReportType) ||
        exportProgressValue > 0 ||
        (exportProgressValue > 0 && !currentExportStatus) ? (
          <Tooltip
            text={l('An export is already in progress for this report type')}
            placement='top'
            isPortalEnabled
            trigger={
              <Button
                key={`export-progress-${reportKey}`}
                kind='filled'
                size='medium'
                isExtendedLoading={
                  hasActiveExport ||
                  (exportProgressValue > 0 && exportProgressValue < 100) ||
                  (exportProgressValue > 0 && !currentExportStatus)
                }
                isSuccess={exportProgressValue >= 100}
                extendedLoadingPercentValue={exportProgressValue}
                onClick={
                  selectedEntitlementValueKey ? handleSyncExport : handleExport
                }
                loaderText={`${l(
                  'Exporting'
                )} (${exportProgressValue.toString()}%)`}
                leftIcon={
                  exportProgressValue >= 100 ? <Icon kind='Check' /> : undefined
                }
                successText={l('Exported')}
                className={cx(
                  (hasActiveExport ||
                    (exportProgressValue > 0 && exportProgressValue < 100)) &&
                    'Sod-Report-Exporting-button Button--isExtendedLoading'
                )}
                isDisabled={
                  isLoading ||
                  isSyncExporting ||
                  isExporting ||
                  !(Array.isArray(reportData) && reportData.length > 0)
                }>
                {l('Export')}
              </Button>
            }
          />
        ) : (
          <Button
            key={`export-normal-${reportKey}`}
            type='button'
            kind='filled'
            onClick={
              selectedEntitlementValueKey ? handleSyncExport : handleExport
            }
            isDisabled={
              isLoading ||
              isSyncExporting ||
              isExporting ||
              !(Array.isArray(reportData) && reportData.length > 0)
            }>
            {l('Export')}
          </Button>
        )}
      </Box>
    </FooterActionBar>
  );

  return (
    <Box
      tag='div'
      className={pageClasses}
      data-testid={testIds.SOD_ENTITLEMENT_TAB_REPORTS_PREVIEW}>
      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </Box>
  );
}

export default EntReportPreview;
